import React, { useEffect, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { PageLoader } from "../components/PageLoader";
import { PageLayout } from "../components/PageLayout";
import { SubscriptionsComponent } from "../components/Subscriptions";
import UserService from "../services/UserService";
import { ROUTES } from "../config/routes";

export const Pricing = () => {
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);

    // If user already has an active subscription, do NOT show pricing.
    // Redirect them straight to the dashboard (same logic as HomePage / CallbackPage).
    useEffect(() => {
        if (!isAuthenticated || !user) {
            setIsCheckingSubscription(false);
            return;
        }

        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const sub = await UserService.getUserSubscription(token, user, true);
                console.log("Pricing: Full subscription response:", JSON.stringify(sub, null, 2));

                let hasActiveSub = false;
                if (sub) {
                    // Primary check: main subscription object
                    const statusIsActive = sub.status === true || sub.status === "true" || sub.status === 1;
                    const hasValidType = sub.subscriptionType && sub.subscriptionType !== "Default";
                    const hasValidId = sub.subscriptionId && String(sub.subscriptionId).trim() !== "";

                    const mainSubCheck = statusIsActive && hasValidType && hasValidId;
                    console.log("Pricing: Main subscription check result:", mainSubCheck);

                    if (mainSubCheck) {
                        hasActiveSub = true;
                        console.log("Pricing: ✅ Active subscription found in main object:", sub.subscriptionType, "ID:", sub.subscriptionId);
                    } else if (Array.isArray(sub.allSubSctiptions) && sub.allSubSctiptions.length > 0) {
                        console.log("Pricing: Checking allSubSctiptions array (length:", sub.allSubSctiptions.length, ")");
                        const activeSub = sub.allSubSctiptions.find((s) => {
                            if (!s) return false;
                            const sStatusIsActive = s.status === true || s.status === "true" || s.status === 1;
                            const sHasValidType = s.subscriptionType && s.subscriptionType !== "Default";
                            const sHasValidId = s.subscriptionId && String(s.subscriptionId).trim() !== "";
                            const sIsValidPlan =
                                s.subscriptionType === "CoPilot" ||
                                s.subscriptionType === "Silver" ||
                                s.subscriptionType === "Gold" ||
                                s.subscriptionType === "Platinum";

                            return sStatusIsActive && sHasValidType && sHasValidId && sIsValidPlan;
                        });

                        if (activeSub) {
                            hasActiveSub = true;
                            console.log(
                                "Pricing: ✅ Active subscription found in allSubSctiptions:",
                                activeSub.subscriptionType,
                                "ID:",
                                activeSub.subscriptionId
                            );
                        } else {
                            console.log("Pricing: ❌ No active subscription found in allSubSctiptions");
                        }
                    } else {
                        console.log("Pricing: ❌ No allSubSctiptions array or it's empty");
                    }
                } else {
                    console.log("Pricing: ❌ Subscription response is null/undefined");
                }

                console.log("Pricing: Final hasActiveSub =", hasActiveSub);

                if (hasActiveSub) {
                    console.log("Pricing: ✅ User already has active subscription, redirecting to Dashboard");
                    navigate(ROUTES.DASHBOARD, { replace: true });
                    return; // Don't set isCheckingSubscription to false, let redirect happen
                }
                
                setIsCheckingSubscription(false);
            } catch (e) {
                console.error("Pricing: Error checking subscription status:", e);
                // On error, keep showing pricing so user can attempt subscription again
                setIsCheckingSubscription(false);
            }
        })();
    }, [isAuthenticated, user, getAccessTokenSilently, navigate]);
    const handleDataChange = (data) => {
        // Handle subscription changes if needed
        console.log("Subscription data changed:", data);
    };

    // Show loader while checking subscription status to prevent flash of pricing page
    if (isCheckingSubscription) {
        return (
            <PageLayout>
                <PageLoader />
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="container-fluid">
                <div className="row justify-content-center py-4">
                    <div className="col-12">
                        <div className="text-center mb-4">
                            <h2 className="mb-3">Choose Your Subscription Plan</h2>
                            <p className="lead">Select the plan that best fits your needs</p>
                        </div>
                        <SubscriptionsComponent 
                            onDataChange={handleDataChange} 
                            sharedData={null}
                        />
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default withAuthenticationRequired(Pricing, {
    onRedirecting: () => <PageLoader />,
});
